import React, { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import "./DevicesMapModal.css";

function parseLngLat(d) {
  const lng =
    d?.longitude ??
    d?.lng ??
    d?.lon ??
    d?.location_longitude ??
    d?.location?.longitude ??
    d?.location?.lng ??
    null;

  const lat =
    d?.latitude ??
    d?.lat ??
    d?.location_latitude ??
    d?.location?.latitude ??
    d?.location?.lat ??
    null;

  const lngN = typeof lng === "string" ? Number(lng) : lng;
  const latN = typeof lat === "string" ? Number(lat) : lat;

  if (Number.isFinite(lngN) && Number.isFinite(latN)) {
    if (lngN >= -180 && lngN <= 180 && latN >= -90 && latN <= 90) {
      return [lngN, latN];
    }
  }
  return null;
}

function safeText(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

export default function DevicesMapModal({
  open,
  onClose,
  devices,
  visibleFields = [],
  isRtl,
  t,
  onOpenDevice,
  alert,
  embedded = false,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const styleControlRef = useRef(null);
  const hasUserFocusedDeviceRef = useRef(false);
  const hasUserViewportOverrideRef = useRef(false);
  const suppressPopupCloseZoomRef = useRef(false);
  const selectedPopupDeviceIdRef = useRef(null);
  const selectedPopupCoordsRef = useRef(null);
  const programmaticViewportChangeRef = useRef(false);
  const programmaticViewportTimeoutRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [onlyLocatedCount, setOnlyLocatedCount] = useState(0);
  const [styleMenuOpen, setStyleMenuOpen] = useState(false);
  const [selectedMapStyleKey, setSelectedMapStyleKey] = useState("satellite");

  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  const mapStyleOptions = useMemo(
    () => [
      {
        key: "streets",
        label: t?.map?.styles?.streets || "Streets",
        style: "mapbox://styles/mapbox/streets-v12",
      },
      {
        key: "satellite",
        label: t?.map?.styles?.satellite || "Satellite",
        style: "mapbox://styles/mapbox/satellite-streets-v12",
      },
      {
        key: "outdoors",
        label: t?.map?.styles?.outdoors || "Outdoors",
        style: "mapbox://styles/mapbox/outdoors-v12",
      },
      {
        key: "light",
        label: t?.map?.styles?.light || "Light",
        style: "mapbox://styles/mapbox/light-v11",
      },
      {
        key: "dark",
        label: t?.map?.styles?.dark || "Dark",
        style: "mapbox://styles/mapbox/dark-v11",
      },
    ],
    [t]
  );

  const selectedMapStyle =
    mapStyleOptions.find((opt) => opt.key === selectedMapStyleKey) || mapStyleOptions[0];
  const visibleFieldSet = useMemo(() => new Set(visibleFields || []), [visibleFields]);

  const geojson = useMemo(() => {
    const features = (devices || [])
      .map((d) => {
        const coords = parseLngLat(d);
        if (!coords) return null;

        const online = String(d?.status || "").toLowerCase() === "online";
        return {
          type: "Feature",
          geometry: { type: "Point", coordinates: coords },
          properties: {
            id: d?._row_id ?? d?.id,
            name: d?.name,
            status: d?.status,
            did: d?.did ?? d?._did,
            tid: d?.tid,
            template_name: d?.template_name,
            main_organization_name: d?.main_organization_name,
            sub_organization_name: d?.sub_organization_name,
            last_reported: d?.last_reported,
            is_online: online ? 1 : 0,
          },
        };
      })
      .filter(Boolean);

    setOnlyLocatedCount(features.length);

    return {
      type: "FeatureCollection",
      features,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  const fitToAll = () => {
    const map = mapRef.current;
    if (!map) return;

    hasUserFocusedDeviceRef.current = false;
    hasUserViewportOverrideRef.current = false;

    if (!geojson?.features?.length) return;

    if (programmaticViewportTimeoutRef.current) {
      clearTimeout(programmaticViewportTimeoutRef.current);
    }
    programmaticViewportChangeRef.current = true;
    programmaticViewportTimeoutRef.current = setTimeout(() => {
      programmaticViewportChangeRef.current = false;
      programmaticViewportTimeoutRef.current = null;
    }, 1000);

    if (geojson.features.length === 1) {
      const c = geojson.features[0].geometry.coordinates;
      map.flyTo({
        center: c,
        zoom: 14,
        padding: { top: 0, bottom: 0, left: 0, right: 0 },
        retainPadding: false,
        essential: true,
      });
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    geojson.features.forEach((f) => {
      bounds.extend(f.geometry.coordinates);
    });

    map.fitBounds(bounds, {
      padding: { top: 90, bottom: 80, left: 80, right: 80 },
      maxZoom: 16,
      duration: 800,
      retainPadding: false,
    });
  };

  const destroyMap = () => {
    try {
      if (popupRef.current) {
        suppressPopupCloseZoomRef.current = true;
        popupRef.current.remove();
        popupRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    } catch (e) {
      // noop
    } finally {
      hasUserFocusedDeviceRef.current = false;
      hasUserViewportOverrideRef.current = false;
      suppressPopupCloseZoomRef.current = false;
      selectedPopupDeviceIdRef.current = null;
      selectedPopupCoordsRef.current = null;
      programmaticViewportChangeRef.current = false;
      if (programmaticViewportTimeoutRef.current) {
        clearTimeout(programmaticViewportTimeoutRef.current);
        programmaticViewportTimeoutRef.current = null;
      }
      setReady(false);
    }
  };

  useEffect(() => {
    if (!open) {
      destroyMap();
      return;
    }

    if (!token) {
      if (alert) {
        alert({
          type: "alert",
          variant: "error",
          icon: "alert",
          title: t?.alert?.errorTitle || "Error",
          message:
            (t?.map?.missingToken ||
              "Missing Mapbox token. Add REACT_APP_MAPBOX_TOKEN to your .env and restart the app.") +
            "",
          confirmText: "Ok",
        }).then(() => onClose && onClose());
      } else {
        onClose && onClose();
      }
      return;
    }

    if (!containerRef.current) return;

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: selectedMapStyle?.style || "mapbox://styles/mapbox/satellite-streets-v12",
      center: [0, 0],
      zoom: 2,
      projection: "mercator",
      renderWorldCopies: false,
      attributionControl: false,
    });

    mapRef.current = map;

    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: false }),
      isRtl ? "top-left" : "top-right"
    );
    map.addControl(
      new mapboxgl.FullscreenControl(),
      isRtl ? "top-left" : "top-right"
    );

    map.on("load", () => {
      setReady(true);

      map.addSource("devices", {
        type: "geojson",
        data: geojson,
      });

      map.addLayer({
        id: "devices-points",
        type: "circle",
        source: "devices",
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2, 4,
            8, 7,
            14, 10,
            18, 12
          ],
          "circle-color": [
            "case",
            ["==", ["get", "is_online"], 1],
            "rgba(26, 204, 124, 0.95)",
            "rgba(170, 170, 170, 0.95)"
          ],
          "circle-stroke-color": "rgba(0,0,0,0.35)",
          "circle-stroke-width": 1,
          "circle-opacity": 1,
        },
      });

      map.addLayer({
        id: "devices-hit",
        type: "circle",
        source: "devices",
        paint: {
          "circle-radius": 16,
          "circle-color": "rgba(0,0,0,0)",
        },
      });

      map.on("mouseenter", "devices-hit", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", "devices-hit", () => {
        map.getCanvas().style.cursor = "";
      });

      const markUserViewportOverride = () => {
        if (programmaticViewportChangeRef.current) return;
        hasUserViewportOverrideRef.current = true;
      };

      map.on("dragstart", markUserViewportOverride);
      map.on("boxzoomstart", markUserViewportOverride);
      map.on("pitchstart", markUserViewportOverride);
      map.on("rotatestart", markUserViewportOverride);
      map.on("zoomstart", (event) => {
        if (event?.originalEvent) {
          markUserViewportOverride();
        }
      });

      map.on("click", "devices-hit", (e) => {
        if (!e?.features?.length) return;

        hasUserFocusedDeviceRef.current = true;

        const nearest = (geojson?.features || []).reduce((best, curr) => {
          const c = curr?.geometry?.coordinates;
          if (!Array.isArray(c) || c.length < 2) return best;
          const projected = map.project([Number(c[0]), Number(c[1])]);
          const dx = projected.x - e.point.x;
          const dy = projected.y - e.point.y;
          const dist = dx * dx + dy * dy;
          if (!best || dist < best.dist) return { feature: curr, dist };
          return best;
        }, null);

        const maxPickDistanceSq = 40 * 40;
        if (!nearest || nearest.dist > maxPickDistanceSq) return;

        const feature = nearest.feature;

        const rawCoords = feature?.geometry?.coordinates;
        if (!Array.isArray(rawCoords) || rawCoords.length < 2) return;

        const coords = [Number(rawCoords[0]), Number(rawCoords[1])];
        if (!Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return;
        const p = feature.properties || {};
        selectedPopupDeviceIdRef.current = p?.id ?? null;
        selectedPopupCoordsRef.current = coords;

        if (programmaticViewportTimeoutRef.current) {
          clearTimeout(programmaticViewportTimeoutRef.current);
        }
        programmaticViewportChangeRef.current = true;
        programmaticViewportTimeoutRef.current = setTimeout(() => {
          programmaticViewportChangeRef.current = false;
          programmaticViewportTimeoutRef.current = null;
        }, 1000);

        map.flyTo({
          center: coords,
          zoom: Math.max(map.getZoom(), 15),
          padding: { top: 0, bottom: 0, left: 0, right: 0 },
          retainPadding: false,
          essential: true,
        });

        const popupRows = [];
        if (visibleFieldSet.has("did")) {
          popupRows.push(`<div class="dmm-k">DID</div><div class="dmm-v">${safeText(p.did)}</div>`);
        }
        if (visibleFieldSet.has("tid")) {
          popupRows.push(`<div class="dmm-k">TID</div><div class="dmm-v">${safeText(p.tid)}</div>`);
        }
        if (visibleFieldSet.has("main_organization_name")) {
          popupRows.push(
            `<div class="dmm-k">${safeText(t?.column?.mainOrganizationName || "Main Organization")}</div><div class="dmm-v">${safeText(p.main_organization_name)}</div>`
          );
        }
        if (visibleFieldSet.has("sub_organization_name")) {
          popupRows.push(
            `<div class="dmm-k">${safeText(t?.column?.subOrganizationName || "Sub Organization")}</div><div class="dmm-v">${safeText(p.sub_organization_name)}</div>`
          );
        }
        if (visibleFieldSet.has("template_name")) {
          popupRows.push(
            `<div class="dmm-k">${safeText(t?.column?.templateName || "Template")}</div><div class="dmm-v">${safeText(p.template_name)}</div>`
          );
        }
        if (visibleFieldSet.has("last_reported")) {
          popupRows.push(
            `<div class="dmm-k">${safeText(t?.column?.reported || "Last reported")}</div><div class="dmm-v">${safeText(p.last_reported)}</div>`
          );
        }

        const html = `
          <div class="dmm-popup" dir="${isRtl ? "rtl" : "ltr"}">
            <div class="dmm-popup-head">
              <div class="dmm-popup-title">${safeText(p.name)}</div>
              <div class="dmm-popup-status">
                <span class="dmm-dot ${String(p.status).toLowerCase() === "online" ? "on" : "off"}"></span>
                <span>${safeText(p.status)}</span>
              </div>
            </div>

            <div class="dmm-popup-grid">
              ${popupRows.join("")}
            </div>

            <div class="dmm-popup-actions">
              <button class="dmm-btn dmm-btn-primary" data-open="1">${safeText(t?.map?.openDevice || "Open device")}</button>
            </div>
          </div>
        `;

        if (popupRef.current) {
          suppressPopupCloseZoomRef.current = true;
          popupRef.current.remove();
          popupRef.current = null;
        }

        const popup = new mapboxgl.Popup({
          closeButton: true,
          closeOnClick: false,
          anchor: "bottom",
          maxWidth: "320px",
          className: "dmm-mapbox-popup",
        })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(map);

        popup.on("close", () => {
          const suppressed = suppressPopupCloseZoomRef.current;
          suppressPopupCloseZoomRef.current = false;
          if (popupRef.current === popup) {
            popupRef.current = null;
          }
          selectedPopupDeviceIdRef.current = null;
          selectedPopupCoordsRef.current = null;
          if (suppressed) return;
          fitToAll();
        });

        popupRef.current = popup;

        setTimeout(() => {
          try {
            const el = popup.getElement();
            if (!el) return;
            const btn = el.querySelector('button[data-open="1"]');
            if (btn) {
              btn.onclick = () => {
                const id = Number(p.id);
                if (onOpenDevice && Number.isFinite(id)) onOpenDevice(id);
              };
            }
          } catch (err) {}
        }, 0);
      });

      fitToAll();
    });

    let onKey = null;
    if (!embedded) {
      onKey = (ev) => {
        if (ev.key === "Escape") onClose && onClose();
      };
      window.addEventListener("keydown", onKey);
    }

    return () => {
      if (onKey) {
        window.removeEventListener("keydown", onKey);
      }
      destroyMap();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, embedded, selectedMapStyle?.style]);

  useEffect(() => {
    if (!styleMenuOpen) return;

    const onDocMouseDown = (event) => {
      if (styleControlRef.current && !styleControlRef.current.contains(event.target)) {
        setStyleMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [styleMenuOpen]);

  useEffect(() => {
    const map = mapRef.current;
    if (!open || !ready || !map) return;

    const src = map.getSource("devices");
    if (src && src.setData) {
      src.setData(geojson);

      const popup = popupRef.current;
      const selectedId = selectedPopupDeviceIdRef.current;
      const selectedCoords = selectedPopupCoordsRef.current;
      if (popup && selectedId !== null && selectedId !== undefined) {
        const idMatched = (geojson?.features || []).filter(
          (f) => String(f?.properties?.id) === String(selectedId)
        );
        const candidates = idMatched.length ? idMatched : geojson?.features || [];
        const selectedFeature = candidates.reduce((best, curr) => {
          const c = curr?.geometry?.coordinates;
          if (!Array.isArray(c) || c.length < 2) return best;
          const prev = selectedCoords;
          if (!Array.isArray(prev) || prev.length < 2) return best || curr;
          const dx = Number(c[0]) - Number(prev[0]);
          const dy = Number(c[1]) - Number(prev[1]);
          const dist = dx * dx + dy * dy;
          if (!best || dist < best.dist) return { feature: curr, dist };
          return best;
        }, null)?.feature || candidates[0];

        if (selectedFeature?.geometry?.coordinates?.length >= 2) {
          const c = selectedFeature.geometry.coordinates;
          const popupCoords = [Number(c[0]), Number(c[1])];
          if (Number.isFinite(popupCoords[0]) && Number.isFinite(popupCoords[1])) {
            popup.setLngLat(popupCoords);
            selectedPopupCoordsRef.current = popupCoords;
          }
        } else {
          suppressPopupCloseZoomRef.current = true;
          popup.remove();
          popupRef.current = null;
          selectedPopupDeviceIdRef.current = null;
          selectedPopupCoordsRef.current = null;
        }
      }

      if (!hasUserFocusedDeviceRef.current && !hasUserViewportOverrideRef.current) {
        fitToAll();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojson, open, ready]);

  if (!open) return null;

  const total = (devices || []).length;

  return (
    <div
      className={embedded ? "dmm-embedded" : "dmm-overlay"}
      role={embedded ? undefined : "dialog"}
      aria-modal={embedded ? undefined : "true"}
    >
      <div className={`dmm-modal${embedded ? " dmm-modal-embedded" : ""}`}>
        <div className="dmm-header">
          <div className="dmm-title">
            <span>{t?.map?.title || "Devices map"}</span>
            <span className="dmm-sub">
              {onlyLocatedCount} / {total} {t?.map?.withLocation || "with location"}
            </span>
          </div>

          <div className="dmm-header-actions">
            <div className="dmm-map-style-wrap" ref={styleControlRef}>
              <button
                type="button"
                className={`dmm-map-style-trigger${styleMenuOpen ? " open" : ""}`}
                onClick={() => setStyleMenuOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={styleMenuOpen}
                title={t?.map?.view || "Map view"}
              >
                <MapOutlinedIcon className="dmm-map-style-icon" />
                <span className="dmm-map-style-label">{selectedMapStyle?.label || "Satellite"}</span>
                <KeyboardArrowDownRoundedIcon className="dmm-map-style-arrow" />
              </button>

              {styleMenuOpen ? (
                <div className="dmm-map-style-menu" role="menu">
                  {mapStyleOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`dmm-map-style-option${
                        selectedMapStyleKey === option.key ? " active" : ""
                      }`}
                      role="menuitemradio"
                      aria-checked={selectedMapStyleKey === option.key}
                      onClick={() => {
                        setSelectedMapStyleKey(option.key);
                        setStyleMenuOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className="dmm-icon-btn"
              title={t?.map?.fitAll || "Fit all"}
              onClick={() => fitToAll()}
            >
              <MyLocationIcon />
            </button>
            {onClose ? (
              <button
                type="button"
                className="dmm-icon-btn"
                title={t?.map?.close || "Close"}
                onClick={onClose}
              >
                <CloseIcon />
              </button>
            ) : null}
          </div>
        </div>

        <div className="dmm-legend" dir={isRtl ? "rtl" : "ltr"}>
          <div className="dmm-leg-item">
            <span className="dmm-leg-dot on" />
            <span>{t?.map?.online || "Online"}</span>
          </div>
          <div className="dmm-leg-item">
            <span className="dmm-leg-dot off" />
            <span>{t?.map?.offline || "Offline"}</span>
          </div>
          <div className="dmm-leg-hint">
            {t?.map?.hint || "Click a point to zoom and view device details."}
          </div>
        </div>

        <div className="dmm-body">
          <div ref={containerRef} className="dmm-map" />
          {!geojson?.features?.length ? (
            <div className="dmm-empty">
              {t?.map?.noLocations ||
                "No device locations found. Make sure your API returns latitude/longitude for devices."}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
